import React, { useState, useEffect, useRef } from 'react';
import {
  Menu as MenuIcon,
  HelpCircle,
  Calendar,
  Settings,
  Shield,
  Building,
  KeyRound,
  Tag,
  Receipt,
  Percent,
  ScrollText,
  Megaphone,
  Ticket,
  SlidersHorizontal,
  MoreVertical,
  BarChart3,
  ExternalLink,
  Pin,
  PinOff,
  Euro,
  Search,
} from 'lucide-react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isCourseEvent } from '../lib/eventTypes';
import { useAuth } from '../contexts/AuthContext';
import VatRegimeBanner from './VatRegimeBanner';
import { useEvent } from '../contexts/EventContext';
import { useOrg } from '../contexts/OrgContext';
import TermsAcceptanceGate from './TermsAcceptanceGate';
import { NotificationBell } from './NotificationBell';
import { AccountMenu } from './AccountMenu';
import { PUBLIC_SITE_URL, ENVIRONMENT } from '../config/api';
import { useProductTour, TOUR_DONE_KEY } from '../hooks/useProductTour';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { cn } from '../lib/utils';
import { useNavSections, isNavItemActive } from './nav/useNavSections';
import { CommandPalette, useCommandPaletteShortcut } from './CommandPalette';
import { useEventOverview, stepsNeedingAttention } from '../hooks/useEventOverview';
// Miroir front de BILLING_ENABLED : les routes des relevés ne sont pas
// montées côté serveur tant que le module comptable n'est pas validé.
const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === 'true';

// Capacités du pointeur, lues UNE fois : le déploiement au survol n'a de sens
// qu'avec un vrai pointeur (souris/trackpad). Sur tactile, `mouseenter` est
// émulé au premier tap → le rail se déployait au lieu de naviguer, et restait
// collé faute de `mouseleave`.
const HOVER_CAPABLE = typeof window !== 'undefined'
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const COARSE_POINTER = typeof window !== 'undefined'
  && window.matchMedia('(pointer: coarse)').matches;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { events, selectedEventId, selectedEvent, setSelectedEventId } = useEvent();
  const { organizations, workingOrgId, setWorkingOrgId, isAdmin: isAdminOrg } = useOrg();
  // Nom de l'organisation de travail — sert de repli au fil d'Ariane mobile.
  const workingOrgName = organizations.find((o) => o.id === workingOrgId)?.name;
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sidebar repliable : repliée par défaut (icônes), déployée au survol (overlay) ou épinglée.
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try {
      const stored = localStorage.getItem('cloudadmin-sidebar-pinned');
      if (stored !== null) return stored === '1';
    } catch { /* noop */ }
    // Sans préférence : sur une tablette en paysage (pointeur grossier, écran
    // large), pas de survol possible → le menu déployé est le seul utilisable.
    return COARSE_POINTER && window.innerWidth >= 1024;
  });
  const [sidebarHover, setSidebarHover] = useState(false);
  // Un menu (sélecteur d'event / kebab) ouvert doit garder la sidebar déployée, même si le
  // survol quitte l'aside (le menu s'affiche en portal → mouseleave sinon → repli intempestif).
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  const sidebarExpanded = sidebarPinned || sidebarHover || sidebarMenuOpen;
  const toggleSidebarPin = () => {
    setSidebarPinned((p) => {
      const next = !p;
      try { localStorage.setItem('cloudadmin-sidebar-pinned', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // Replie la sidebar à CHAQUE changement de route (peu importe la source : item de menu,
  // bouton d'un modal, navigation programmatique). Le hover peut rester « collé » quand un
  // modal/portal s'ouvre par-dessus l'aside (mouseleave non déclenché).
  useEffect(() => {
    setSidebarHover(false);
    setSidebarMenuOpen(false);
  }, [location.pathname]);

  const { startTour } = useProductTour();

  // Changement d'événement depuis le dropdown. Si on est sur une page
  // résultats/scores, on bascule la page vers le même type pour le nouvel
  // événement (selon son event_type). Sinon, on met juste à jour le contexte.
  const handleEventChange = (value: string) => {
    const newId = value === '__none__' ? null : value;
    setSelectedEventId(newId);
    if (!newId) return;
    if (/^\/admin\/events\/[^/]+\/settings$/.test(location.pathname)) {
      navigate(`/admin/events/${newId}/settings`);
      return;
    }
    const onResultsPage = /^\/admin\/events\/[^/]+\/(scores|crossfit-results|race-results)$/.test(location.pathname);
    if (!onResultsPage) return;
    // La page « Résultats » (crossfit-results) fusionne consultation + édition des scores ;
    // /scores redirige vers elle. On bascule juste vers le bon type selon l'event.
    const isCourse = isCourseEvent(events.find((e) => e.id === newId)?.event_type);
    const target = isCourse ? 'race-results' : 'crossfit-results';
    navigate(`/admin/events/${newId}/${target}`);
  };

  // Actions du kebab (⋮) accolé au dropdown — agissent sur l'événement sélectionné.
  // L'édition réutilise le deep-link ?edit= déjà géré par la page Événements
  // (le formulaire d'édition n'existe que là) → une seule source de vérité.
  const publicEventUrl = selectedEvent?.slug
    ? `${PUBLIC_SITE_URL}/events/${selectedEvent.slug}`
    : null;

  const goEditEvent = () => {
    if (!selectedEventId) return;
    navigate(`/admin/events/${selectedEventId}/settings`);
    setMobileOpen(false);
  };

  const goEventResults = () => {
    if (!selectedEvent) return;
    const kind = isCourseEvent(selectedEvent.event_type) ? 'race-results' : 'crossfit-results';
    navigate(`/admin/events/${selectedEvent.id}/${kind}`);
    setMobileOpen(false);
  };

  const openPublicPage = () => {
    if (!publicEventUrl) return;
    window.open(publicEventUrl, '_blank', 'noopener,noreferrer');
    setMobileOpen(false);
  };

  // Prénom pour le message d'accueil (fallback : partie locale de l'email)
  const greetingName = user?.first_name || user?.email?.split('@')[0] || '';

  // Ref vers le startTour courant : évite de re-déclencher l'auto-lancement quand
  // startTour change d'identité (deps events/selectedEventId) pendant le tour.
  const startTourRef = useRef(startTour);
  startTourRef.current = startTour;
  const autoStartedRef = useRef(false);

  // Lancement auto du tour au 1er login (organisateurs, EXACTEMENT une fois par montage).
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!user || user.role === 'participant') return;
    if (window.innerWidth < 640) return; // tour = expérience desktop uniquement
    if (localStorage.getItem(TOUR_DONE_KEY)) return;
    autoStartedRef.current = true;
    // Laisse le temps à la sidebar + page de se monter avant de pointer les ancres.
    const timer = setTimeout(() => startTourRef.current(), 800);
    return () => clearTimeout(timer);
  }, [user]);

  // Menu items généraux (admin uniquement). "Tableau de bord" retiré du menu
  // (écran/route conservés). "Événements" déplacé sous la section GESTION D'ÉVÉNEMENT.
  const generalMenuItems = [
    ...(user?.role === 'admin' ? [{ text: t('nav.userManagement'), icon: <Shield size={18} />, path: '/admin/users' }] : []),
    ...(user?.role === 'admin' ? [
      { text: t('nav.licenses'), icon: <KeyRound size={18} />, path: '/admin/licenses' },
      { text: t('nav.promoCodes'), icon: <Tag size={18} />, path: '/admin/promo-codes' },
      { text: t('nav.appPromos'), icon: <Megaphone size={18} />, path: '/admin/app-promos' },
      { text: t('nav.kartCodes'), icon: <Ticket size={18} />, path: '/admin/kart-codes' },
      { text: t('nav.appFeatureFlags'), icon: <SlidersHorizontal size={18} />, path: '/admin/app-feature-flags' },
      { text: t('nav.transactions'), icon: <Receipt size={18} />, path: '/admin/transactions' },
      { text: t('nav.revenue'), icon: <Euro size={18} />, path: '/admin/revenue' },
      { text: t('nav.vatRegimes'), icon: <Percent size={18} />, path: '/admin/vat-regimes' },
      { text: t('nav.adminBilling'), icon: <ScrollText size={18} />, path: '/admin/billing' },
    ] : []),
  ];

  // Menu de l'événement, rangé par moment (Préparer · Participants · Jour J).
  // Même définition que la palette ⌘K : useNavSections.
  const navSections = useNavSections();
  const { data: overview } = useEventOverview(selectedEventId);
  const attention = stepsNeedingAttention(overview);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteShortcut(setPaletteOpen);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  // Le raccourci ne s'annonce qu'avec une souris ou un trackpad : sur un
  // téléphone ou une tablette tactile il n'y a pas de clavier pour le taper,
  // et un iPhone se voyait proposer « ⌘K » (2026-09-28).
  const raccourci = typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ? (isMac ? '⌘K' : 'Ctrl K')
    : null;

  // Titre de la page courante, dérivé des items de navigation (une seule
  // source pour le libellé : celle qui l'affiche déjà dans le menu).
  const navTitleItems: { text: string; path: string }[] = [
    ...generalMenuItems,
    { text: t('nav.events'), path: '/admin/events' },
    ...navSections.flatMap((sec) => sec.items),
    { text: t('nav.settings'), path: '/admin/settings' },
    { text: t('nav.archives'), path: '/admin/archives' },
    { text: t('nav.statements'), path: '/admin/statements' },
    { text: t('nav.myLicense'), path: '/admin/my-license' },
  ];
  const currentPageTitle: string | undefined = (() => {
    const hit = navTitleItems.find((i) => i.path === location.pathname);
    if (hit) return hit.text;
    const matched = navSections.flatMap((sec) => sec.items).find((i) => isNavItemActive(i, location.pathname));
    return matched?.text;
  })();

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    // Replie la sidebar après navigation (le hover peut rester « collé » quand un
    // modal/portal s'est ouvert par-dessus l'aside sans déclencher mouseleave).
    setSidebarHover(false);
  };

  const NavItem = ({ item, isActive, collapsed, needsAttention }: { item: { text: string; icon: React.ReactNode; path: string; dataTour?: string }; isActive: boolean; collapsed?: boolean; needsAttention?: boolean }) => {
    if (collapsed) {
      // Repliée : icône seule + tooltip (zéro layout shift, standard UI moderne)
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleNavigation(item.path)}
              data-tour={item.dataTour}
              aria-label={needsAttention ? `${item.text} · ${t('nav.needsAttention')}` : item.text}
              className={cn(
                "relative w-full flex items-center justify-center h-11 rounded-md transition-colors",
                isActive
                  ? "text-foreground bg-white/[0.07]"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
              )}
            >
              {item.icon}
              {needsAttention && <span aria-hidden className="absolute top-2.5 right-3 h-1.5 w-1.5 rounded-full bg-warning" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right"><p className="text-xs">{item.text}</p></TooltipContent>
        </Tooltip>
      );
    }
    return (
      <button
        onClick={() => handleNavigation(item.path)}
        data-tour={item.dataTour}
        className={cn(
          "w-full flex items-center gap-3 px-3 h-11 rounded-md text-sm transition-colors",
          isActive
            ? "text-foreground font-semibold bg-white/[0.07]"
            : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
        )}
      >
        <span className={cn(isActive ? "text-foreground" : "text-muted-foreground")}>
          {item.icon}
        </span>
        <span className="flex-1 text-left truncate">{item.text}</span>
        {needsAttention && (
          <span className="h-1.5 w-1.5 rounded-full bg-warning" aria-label={t('nav.needsAttention')} />
        )}
      </button>
    );
  };

  // En-tête de section — hauteur FIXE identique replié/déployé (pas de décalage des icônes).
  // Déployé : séparateurs + texte. Replié : un simple filet centré, même hauteur.
  const SectionHeader = ({ label, collapsed, className }: { label: string; collapsed?: boolean; className?: string }) => (
    <div className={cn("flex items-center gap-2 px-3 h-5", className)}>
      {collapsed ? (
        <Separator className="flex-1" />
      ) : (
        <>
          <Separator className="flex-1" />
          <span className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-widest whitespace-nowrap">
            {label}
          </span>
          <Separator className="flex-1" />
        </>
      )}
    </div>
  );

  // URL vers logfit.eu - les cookies HTTP-only sont partagés sur .logfit.eu
  const getPublicSiteUrl = () => PUBLIC_SITE_URL;

  const SidebarContent = ({ collapsed = false }: { collapsed?: boolean }) => (
    <div className="relative flex flex-col h-full">
      {/* Logo */}
      <div className={cn("h-20 flex items-center justify-center border-b border-border", collapsed ? "px-2 flex-col gap-1" : "px-3")}>
        {/* Replié : le même basculement d'épingle, en ☰ au-dessus du logo — sur
            tactile c'est le SEUL moyen de déployer le menu (pas de survol). */}
        {collapsed && (
          <button
            onClick={toggleSidebarPin}
            aria-label={sidebarPinned ? t('nav.sidebarUnpin') : t('nav.sidebarPin')}
            title={sidebarPinned ? t('nav.sidebarUnpin') : t('nav.sidebarPin')}
            className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
          >
            <MenuIcon size={16} />
          </button>
        )}
        <a href={getPublicSiteUrl()} className="flex items-center justify-center min-w-0">
          <img
            src={`${PUBLIC_SITE_URL}/logos/${collapsed ? 'LOGFIT-LOGO-ONLY-PATH.svg' : 'LOGFIT-LOGO-ORANGE-PATH.svg'}`}
            alt="LOGFIT"
            className={collapsed ? "h-9 w-9 object-contain" : "h-16 w-full object-contain"}
          />
        </a>
      </div>

      {/* Épingle — en haut à droite du menu (positionnée en absolu → n'ajoute aucune hauteur,
          donc pas de décalage des icônes). Visible seulement quand la sidebar est déployée. */}
      {!collapsed && (
        <button
          onClick={toggleSidebarPin}
          aria-label={sidebarPinned ? t('nav.sidebarUnpin') : t('nav.sidebarPin')}
          title={sidebarPinned ? t('nav.sidebarUnpin') : t('nav.sidebarPin')}
          className={cn(
            "absolute top-[88px] right-2 z-10 h-7 w-7 flex items-center justify-center transition-colors",
            sidebarPinned ? "text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {sidebarPinned ? <PinOff size={15} /> : <Pin size={15} />}
        </button>
      )}

      {/* Navigation */}
      <nav className={cn("flex-1 py-4 overflow-y-auto overflow-x-hidden", collapsed ? "px-2" : "px-3")}>
        {/* Menu général */}
        <div className="space-y-1">
          {generalMenuItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              isActive={location.pathname === item.path}
              collapsed={collapsed}
            />
          ))}
        </div>

        {/* Organisation de travail — MOBILE uniquement : sur desktop le sélecteur
            vit dans le fil d'Ariane de l'appbar, qui n'a pas la place sur petit
            écran. Il reste SOUS les entrées Admin (elles ne dépendent d'aucune
            organisation) et AU-DESSUS de la gestion d'événement : on lit le tiroir
            dans l'ordre où l'on choisit, organisation › événement › page. */}
        {isAdminOrg && !collapsed && (
          <div className="sm:hidden px-1 mt-3 mb-1 h-11 flex items-center">
            <Select
              value={workingOrgId || '__none__'}
              onValueChange={(v) => setWorkingOrgId(v === '__none__' ? null : v)}
            >
              <SelectTrigger className="flex-1 min-w-0 bg-background border-border">
                <SelectValue placeholder={t('nav.selectOrganization')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-muted-foreground">{t('nav.noOrganization')}</span>
                </SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    <span className="truncate">{org.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* --- GESTION D'ÉVÉNEMENT : Événements + sélecteur --- */}
        <SectionHeader label={t('nav.eventManagement')} collapsed={collapsed} className="mt-2 mb-1" />
        <div className="space-y-1">
          <NavItem
            item={{ text: t('nav.events'), icon: <Calendar size={18} />, path: '/admin/events', dataTour: 'nav-events' }}
            isActive={location.pathname === '/admin/events'}
            collapsed={collapsed}
          />
        </div>
        <div className={cn("px-1 mt-1 mb-2 h-11 flex items-center", collapsed && "justify-center")} data-tour="event-selector">
          {collapsed ? (
            <button
              onClick={() => handleNavigation('/admin/events')}
              aria-label={t('nav.selectEvent')}
              title={selectedEvent?.name || t('nav.selectEvent')}
              className="relative h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
            >
              <Calendar size={18} />
              {selectedEventId && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-primary" />}
            </button>
          ) : (
          <div className="flex-1 min-w-0 flex items-center gap-1">
            <Select
              value={selectedEventId || '__none__'}
              onValueChange={handleEventChange}
              onOpenChange={setSidebarMenuOpen}
            >
              <SelectTrigger className="flex-1 min-w-0 bg-background border-border">
                <SelectValue placeholder={t('nav.selectEvent')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-muted-foreground">{t('nav.noEvent')}</span>
                </SelectItem>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {/* Le nom seul : l'ancien point vert/jaune disait le type
                        d'épreuve, qui n'est pas un état et prenait la place du
                        nom dans le champ fermé. */}
                    <span className="truncate">{event.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Kebab d'actions sur l'événement sélectionné (édition, résultats, page publique) */}
            {selectedEventId && (
              <DropdownMenu onOpenChange={setSidebarMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label={t('nav.eventActions')}
                    className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
                  >
                    <MoreVertical size={18} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={goEditEvent}>
                    <Settings size={16} className="mr-2" />
                    {t('nav.eventSettings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={goEventResults}>
                    <BarChart3 size={16} className="mr-2" />
                    {t('events.viewResults')}
                  </DropdownMenuItem>
                  {publicEventUrl && (
                    <DropdownMenuItem onClick={openPublicPage}>
                      <ExternalLink size={16} className="mr-2" />
                      {t('nav.viewPublicPage')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          )}
        </div>

        {/* Recherche / palette ⌘K */}
        <div className={cn("px-1 mb-2", collapsed && "flex justify-center")} data-tour="command-palette">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setPaletteOpen(true)}
                  aria-label={t('commandPalette.open')}
                  className="h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
                >
                  <Search size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right"><p className="text-xs">{t('commandPalette.open')}{raccourci ? ` · ${raccourci}` : ''}</p></TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={() => setPaletteOpen(true)}
              className="w-full h-9 flex items-center gap-2 rounded-md border border-white/[0.10] px-3 text-sm text-muted-foreground hover:text-foreground hover:border-white/20 transition-colors"
            >
              <Search size={15} />
              <span className="flex-1 text-left">{t('commandPalette.open')}</span>
              {raccourci && <kbd className="rounded-sm border border-white/[0.14] px-1.5 text-[11px] font-sans">{raccourci}</kbd>}
            </button>
          )}
        </div>

        {/* Menu de l'événement — par moment */}
        {navSections.map((section) => (
          <React.Fragment key={section.key}>
            {section.label && <SectionHeader label={section.label} collapsed={collapsed} className="mt-3 mb-1" />}
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavItem
                  key={item.key}
                  item={item}
                  isActive={isNavItemActive(item, location.pathname)}
                  collapsed={collapsed}
                  needsAttention={!!item.step && attention.has(item.step)}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </nav>

      {/* Paramètres, Archives et Facturation vivent dans le menu de compte de
          l'app bar : ils concernent l'organisation, pas l'événement choisi. */}
      {/* Version — LOG-95. Hauteur réservée dans les 2 états (replié/déployé) pour que la zone
          de scroll de la nav ait la même hauteur → pas de décalage des icônes en bas au scroll. */}
      <div className="h-6 flex items-center justify-center px-2 text-[10px] leading-none text-muted-foreground/50 select-text">
        {!collapsed && <span>LogFit® Pro {process.env.REACT_APP_VERSION || 'dev'}</span>}
      </div>
    </div>
  );

  const envBanner = ENVIRONMENT !== 'production' ? {
    label: ENVIRONMENT === 'staging' ? 'STAGING' : 'LOCAL DEV',
    color: ENVIRONMENT === 'staging' ? 'bg-warning text-warning-foreground' : 'bg-info text-info-foreground',
  } : null;

  return (
    <div className="flex min-h-screen bg-background">
      {/* Environment Banner */}
      {envBanner && (
        <div className={`fixed top-0 left-0 right-0 z-50 ${envBanner.color} text-center text-xs font-bold py-1 tracking-widest`}>
          {envBanner.label}
        </div>
      )}

      {/* Sidebar - Desktop : repliée (icônes) par défaut, déployée au survol (overlay) ou épinglée.
          z-50 pour passer au-dessus du header quand elle se déploie en overlay. */}
      <aside
        {...(HOVER_CAPABLE ? {
          onMouseEnter: () => setSidebarHover(true),
          onMouseLeave: () => setSidebarHover(false),
        } : {})}
        className={cn(
          "hidden sm:flex flex-col fixed inset-y-0 z-50 border-r border-border bg-card transition-[width] duration-200 ease-out",
          sidebarExpanded ? "w-60 shadow-xl shadow-black/40" : "w-16",
          envBanner && "top-7"
        )}
      >
        {SidebarContent({ collapsed: !sidebarExpanded })}
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 p-0 bg-card overflow-hidden">
          {/* pt-10 pour éviter le chevauchement avec le bouton X, h-full pour que SidebarContent scroll */}
          <div className="pt-10 h-full flex flex-col overflow-hidden">
            {SidebarContent({})}
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Content — la marge réserve la largeur de la sidebar repliée (w-16) ou épinglée (w-60).
          Le survol déploie la sidebar en overlay sans pousser le contenu (pas de reflow). */}
      <div className={cn("flex-1 min-w-0 transition-[margin] duration-200 ease-out", sidebarPinned ? "sm:ml-60" : "sm:ml-16", envBanner && "mt-7")}>
        {/* Top Navbar */}
        <header className={cn(
          "sticky z-40 h-14 sm:h-16 bg-card flex items-center px-2 sm:px-4 gap-1 sm:gap-4",
          // Fondu de bord : le contenu défile SOUS un dégradé, pas sous un filet dur.
          "after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-4 after:bg-gradient-to-b after:from-background/85 after:to-transparent",
          envBanner ? "top-7" : "top-0",
        )}>
          {/* Mobile Menu Button */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="sm:hidden p-2 hover:bg-muted rounded-md">
                <MenuIcon size={24} />
              </button>
            </SheetTrigger>
          </Sheet>

          {/* CTA for participants */}
          {user?.role === 'participant' && (
            <Button
              onClick={() => navigate('/become-organizer')}
              className="hidden md:flex"
            >
              <Building size={18} className="mr-2" />
              {t('nav.createFirstEvent')}
            </Button>
          )}

          {/* Mobile : le contexte de travail courant. L'événement quand il y en a un,
              sinon l'organisation choisie — sans quoi un admin qui vient de changer
              d'organisation n'en a aucune trace tant qu'il n'a pas ouvert le tiroir. */}
          {(selectedEvent || workingOrgName) && (
            <span className="sm:hidden text-xs text-muted-foreground truncate max-w-[140px]">
              {selectedEvent ? selectedEvent.name : workingOrgName}
            </span>
          )}

          {/* Desktop : Événement › Page courante — l'événement en cours doit se
              lire sans déployer la sidebar. Le message d'accueil ne reste que
              quand aucun événement n'est sélectionné. */}
          {/* Organisation de travail — ADMIN uniquement, en TÊTE du fil
              d'Ariane : « Organisation › Événement › Page » se lit dans l'ordre
              où l'on choisit. Même grammaire visuelle que le sélecteur
              d'événement, sans icône ni couleur d'alerte — ne pas avoir encore
              choisi n'est pas une erreur, les écrans concernés le disent. */}
          {isAdminOrg && (
            <Select
              value={workingOrgId || '__none__'}
              onValueChange={(v) => setWorkingOrgId(v === '__none__' ? null : v)}
            >
              {/* Rendu en TEXTE, pas en champ : c'est un fil d'Ariane, pas un
                  formulaire. Ni cadre ni fond, et l'anneau de focus jaune (--ring)
                  est remplacé par un simple soulignement — visible au clavier, muet
                  à la souris. Le chevron suffit à dire que c'est ouvrable. */}
              <SelectTrigger className="hidden sm:flex h-auto w-auto max-w-[220px] gap-1 border-0 bg-transparent p-0 text-sm text-muted-foreground transition-colors hover:text-foreground focus:ring-0 focus:ring-offset-0 focus-visible:underline focus-visible:underline-offset-4">
                <SelectValue placeholder={t('nav.selectOrganization')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-muted-foreground">{t('nav.noOrganization')}</span>
                </SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    <span className="truncate">{org.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isAdminOrg && (selectedEvent || currentPageTitle) && (
            <span className="hidden sm:block text-muted-foreground" aria-hidden>›</span>
          )}

          {selectedEvent ? (
            <span className="hidden sm:flex items-center gap-1.5 min-w-0 text-sm">
              <b className="font-semibold text-foreground truncate max-w-[260px]">{selectedEvent.name}</b>
              {currentPageTitle && (
                <>
                  <span className="text-muted-foreground" aria-hidden>›</span>
                  <span className="text-muted-foreground truncate">{currentPageTitle}</span>
                </>
              )}
            </span>
          ) : greetingName && (
            <span className="hidden sm:block text-base font-medium text-foreground truncate max-w-[280px]">
              {t('nav.greeting', { name: greetingName })}
            </span>
          )}

          <div className="flex-1" />

          {/* Help / relancer le tour */}
          <button
            data-tour="help-button"
            onClick={() => startTour()}
            title={t('tour.helpButtonTitle')}
            aria-label={t('tour.helpButtonTitle')}
            className="hidden sm:block p-2 hover:bg-muted rounded-md transition-colors text-muted-foreground hover:text-foreground"
          >
            <HelpCircle size={20} />
          </button>

          {/* Notification Bell */}
          <NotificationBell />

          {/* Un seul menu de compte, au nom de l'organisation consultée :
              organisation, jetons, facturation, archives, profil, langue. */}
          <AccountMenu
            organizationName={workingOrgName || user?.organization?.name || null}
            email={user?.email}
            isParticipant={user?.role === 'participant'}
            billingEnabled={BILLING_ENABLED}
            onLogout={logout}
          />
        </header>

        {/* Page Content */}
        {/* `clip` et non `hidden` : les deux coupent le débordement horizontal des
          tables larges (correction du 2026-02-26), mais `hidden` fait calculer
          `overflow-y: auto` au navigateur, ce qui transforme <main> en conteneur
          de défilement. Tout `position: sticky` à l'intérieur se cale alors sur
          lui — et comme <main> ne défile jamais lui-même, l'élément part avec la
          page. C'est ce qui neutralisait le rail des équipes à placer.
          Mesuré : après 572 px de défilement, le rail passait de 16 à -556 px en
          `hidden`, et reste à 16 en `clip`. */}
        <main className="min-h-[calc(100vh-4rem)] overflow-x-clip">
          {/* Rappel permanent tant que le régime TVA n'est pas déclaré. Dans le
              flux et non `fixed` : la bannière d'environnement occupe déjà le
              haut de la fenêtre et les décalages (top-7 / mt-7) sont calculés
              sur elle seule. */}
          <VatRegimeBanner />
          {/* `key` sur l'organisation de travail : en changer remonte l'écran,
              donc TOUT se recharge. Les pages « organisation » chargent leurs
              données dans un `useEffect(…, [])` — sans ça, changer
              d'organisation laissait affichées les valeurs de la précédente
              (constaté : le n° de TVA de Pax Technologies sous Cosmos Gym).
              Le faire ici plutôt que dans chaque page, c'est la garantie de
              n'en oublier aucune. */}
          <Outlet key={workingOrgId || 'compte'} />
        </main>
          <TermsAcceptanceGate />
          <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      </div>
    </div>
  );
};

export default Layout;
